import { z } from 'zod';
import { FlowAdvancedFhirBlockSchema } from './advancedFhir/config.js';
import { FlowAsynchronousExportJobBlockSchema } from './asynchronousExportJob/config.js';
import { FlowAsynchronousExtractJobBlockSchema } from './asynchronousExtractJob/config.js';
import { FlowAuthorizationSubmissionBlockSchema } from './authorizationSubmission/config.js';
import { FlowAutomatedVoiceBlockSchema } from './automatedVoice/config.js';
import { FlowCallbackBlockSchema } from './callback/config.js';
import { FlowCareGapAlgorithmBlockSchema } from './careGapAlgorithm/config.js';
import { FlowConditionalBlockSchema } from './conditional/config.js';
import { FlowConversationBlockSchema } from './conversation/config.js';
import { FlowCreateEncounterSummaryBlockSchema } from './createEncounterSummary/config.js';
import { FlowCreateNotableObjectBlockSchema } from './createNotableObject/config.js';
import { FlowCreatePatientOrderBlockSchema } from './createPatientOrder/config.js';
import { FlowDocumentExtractBlockSchema } from './documentExtract/config.js';
import { FlowDocumentSearchBlockSchema } from './documentSearch/config.js';
import { FlowDocumentTemplateBlockSchema } from './documentTemplate/config.js';
import { FlowEvaluationBlockSchema } from './evaluation/config.js';
import { FlowExportExternalDocumentPacketBlockSchema } from './exportExternalDocumentPacket/config.js';
import { FlowExportTextToBucketBlockSchema } from './exportTextToBucket/config.js';
import { FlowExportToSftpBlockSchema } from './exportToSftp/config.js';
import { FlowFaxBlockSchema } from './fax/config.js';
import { FlowFhirBlockSchema } from './fhir/config.js';
import { FlowFileConversionBlockSchema } from './fileConversion/config.js';
import { FlowFilterListBlockSchema } from './filterList/config.js';
import { FlowFormBlockSchema } from './form/config.js';
import { FlowFormV2BlockSchema } from './form/configV2.js';
import { FlowGenerateFileBlockSchema } from './generateFile/config.js';
import { FlowGetExternalDocumentPacketBlockSchema } from './getExternalDocumentPacket/config.js';
import { FlowGetFormSetBlockSchema } from './getFormSet/config.js';
import { FlowGetInsurancesBlockSchema } from './getInsurances/config.js';
import { FlowGetNotableObjectBlockSchema } from './getNotableObject/config.js';
import { FlowGetPatientBlockSchema } from './getPatient/config.js';
import { FlowGetPatientOrderBlockSchema } from './getPatientOrder/config.js';
import { FlowGetProviderBlockSchema } from './getProvider/config.js';
import { FlowGetSftpFileBlockSchema } from './getSftpFile/config.js';
import { FlowHccAlgorithmBlockSchema } from './hccAlgorithm/config.js';
import { FlowHieGetDocumentsBlockSchema } from './hieGetDocuments/config.js';
import { FlowHieQueryDocumentsBlockSchema } from './hieQueryDocuments/config.js';
import { FlowHl7BlockSchema } from './hl7/config.js';
import { FlowHumanTaskBlockSchema } from './humanTask/config.js';
import { FlowInformationExtractionBlockSchema } from './informationExtraction/config.js';
import { FlowInsuranceCardBlockSchema } from './insuranceCard/config.js';
import { FlowIteratorV1BlockSchema } from './iterator/v1/config.js';
import { FlowIteratorV2BlockSchema } from './iterator/v2/config.js';
import { FlowLlmPromptBlockSchema } from './llmPrompt/config.js';
import { FlowMedicalNecessityCheckBlockSchema } from './medicalNecessityCheck/config.js';
import { FlowMirthBlockSchema } from './mirth/config.js';
import { FlowMontageOnBaseBlockSchema } from './montageOnBase/config.js';
import { FlowOcrBlockSchema } from './ocr/v1/config.js';
import { FlowOcrV2BlockSchema } from './ocr/v2/config.js';
import { FlowOutputBlockSchema } from './output/config.js';
import { FlowPatientDataStoreBlockSchema } from './patientDataStore/config.js';
import { FlowRealTimeEligibilityBlockSchema } from './realTimeEligibility/config.js';
import { FlowRepeatBlockSchema } from './repeat/config.js';
import { FlowRestBlockSchema } from './rest/config.js';
import { FlowRestApiClassicAuthorizationBlockSchema } from './restApiClassicAuthorization/config.js';
import { FlowSchedulingBlockSchema } from './scheduling/config.js';
import { FlowScriptedBlockSchema } from './script/config.js';
import { FlowSendEmailBlockSchema } from './sendEmail/config.js';
import { FlowSendSmsBlockSchema } from './sendSms/config.js';
import { FlowServiceReviewBlockSchema } from './serviceReview/config.js';
import { FlowSkillV2BlockSchema } from './skill/v2/config.js';
import { FlowStartBlockSchema } from './start/config.js';
import { FlowSubflowV1BlockSchema } from './subflow/v1/config.js';
import { FlowSubflowV2BlockSchema } from './subflow/v2/config.js';
import { FlowSwitchBlockSchema } from './switch/config.js';
import { FlowTextBlockSchema } from './text/config.js';
import { FlowUpdateFlowConfigSchema } from './updateFlowConfig/config.js';
import { FlowUpdateNotableObjectBlockSchema } from './updateNotableObject/config.js';
import { FlowVoiceCallBlockSchema } from './voiceCall/config.js';
import { FlowVoiceCallV2BlockSchema } from './voiceCall/configV2.js';
import { FlowVoiceTranscribeBlockSchema } from './voiceTranscribe/config.js';
import { FlowWaitBlockSchema } from './wait/v1/config.js';
import { FlowWaitBlockV2Schema } from './wait/v2/config.js';
import { FlowWaitForFormCompletionBlockSchema } from './waitForFormCompletion/config.js';
import { FlowWaitForSignalBlockSchema } from './waitForSignal/config.js';
/**
 * Every block, discriminated on `type`.
 *
 * Structural validation only, and synchronous. flow-api layers the database-backed checks (a
 * sub-flow's published version, a secure header's domain) onto each request body that carries
 * blocks, because those answer a question about stored state rather than about the block itself.
 *
 * Annotated rather than inferred: several configs apply defaults and transforms, so the input type
 * is looser than {@link FlowBlock} and writing it out would mean a second type per block.
 */
const flowBlockUnion = z.discriminatedUnion('type', [
    FlowCallbackBlockSchema,
    FlowCareGapAlgorithmBlockSchema,
    FlowConditionalBlockSchema,
    FlowConversationBlockSchema,
    FlowCreateEncounterSummaryBlockSchema,
    FlowCreatePatientOrderBlockSchema,
    FlowDocumentExtractBlockSchema,
    FlowDocumentTemplateBlockSchema,
    FlowGenerateFileBlockSchema,
    FlowAsynchronousExtractJobBlockSchema,
    FlowAsynchronousExportJobBlockSchema,
    FlowEvaluationBlockSchema,
    FlowExportExternalDocumentPacketBlockSchema,
    FlowExportTextToBucketBlockSchema,
    FlowFaxBlockSchema,
    FlowFilterListBlockSchema,
    FlowFhirBlockSchema,
    FlowFormBlockSchema,
    FlowFormV2BlockSchema,
    FlowGetExternalDocumentPacketBlockSchema,
    FlowGetFormSetBlockSchema,
    FlowGetPatientBlockSchema,
    FlowGetPatientOrderBlockSchema,
    FlowGetNotableObjectBlockSchema,
    FlowAuthorizationSubmissionBlockSchema,
    FlowGetProviderBlockSchema,
    FlowHccAlgorithmBlockSchema,
    FlowHumanTaskBlockSchema,
    FlowInsuranceCardBlockSchema,
    FlowIteratorV1BlockSchema,
    FlowIteratorV2BlockSchema,
    FlowRepeatBlockSchema,
    FlowLlmPromptBlockSchema,
    FlowOcrBlockSchema,
    FlowOcrV2BlockSchema,
    FlowMedicalNecessityCheckBlockSchema,
    FlowPatientDataStoreBlockSchema,
    FlowRestBlockSchema,
    FlowSchedulingBlockSchema,
    FlowScriptedBlockSchema,
    FlowSendEmailBlockSchema,
    FlowSendSmsBlockSchema,
    FlowAutomatedVoiceBlockSchema,
    FlowServiceReviewBlockSchema,
    FlowStartBlockSchema,
    FlowSubflowV1BlockSchema,
    FlowSubflowV2BlockSchema,
    FlowSkillV2BlockSchema,
    FlowSwitchBlockSchema,
    FlowUpdateFlowConfigSchema,
    FlowUpdateNotableObjectBlockSchema,
    FlowCreateNotableObjectBlockSchema,
    FlowVoiceCallBlockSchema,
    FlowVoiceCallV2BlockSchema,
    FlowVoiceTranscribeBlockSchema,
    FlowWaitBlockSchema,
    FlowWaitBlockV2Schema,
    FlowWaitForSignalBlockSchema,
    FlowWaitForFormCompletionBlockSchema,
    FlowInformationExtractionBlockSchema,
    FlowHl7BlockSchema,
    FlowHieQueryDocumentsBlockSchema,
    FlowHieGetDocumentsBlockSchema,
    FlowGetInsurancesBlockSchema,
    FlowDocumentSearchBlockSchema,
    FlowOutputBlockSchema,
    FlowRestApiClassicAuthorizationBlockSchema,
    FlowGetSftpFileBlockSchema,
    FlowRealTimeEligibilityBlockSchema,
    FlowMirthBlockSchema,
    FlowMontageOnBaseBlockSchema,
    FlowAdvancedFhirBlockSchema,
    FlowFileConversionBlockSchema,
    FlowTextBlockSchema,
    FlowExportToSftpBlockSchema,
]);
export const FlowBlockUnionSchema = flowBlockUnion;
/**
 * Block schemas keyed by their `type` discriminator. Derived from the union's own members, not from
 * the FlowBlockType enum, which is a superset that includes types with no config schema.
 */
export const flowBlockSchemasByType = Object.fromEntries(flowBlockUnion.options.map((option) => [option.shape.type.value, option]));
//# sourceMappingURL=union.js.map